import { ImportRequestScanResult, ImportRequestSource, ImportRequestStatus } from './enums'
import type {
  ExternalImportVerificationDetail,
  ExternalImportVerificationListItem,
  ImportRequestObjectCounts,
  ImportRequestVerificationDetail,
  ImportRequestVerificationListItem,
  UploadedImportVerificationDetail,
  UploadedImportVerificationListItem
} from './types'

export const isExternalImportRequest = (
  request: Pick<ImportRequestVerificationListItem, 'source'>
): request is ExternalImportVerificationListItem => request.source === ImportRequestSource.ExternalS3Location

export const isUploadedImportRequest = (
  request: Pick<ImportRequestVerificationListItem, 'source'>
): request is UploadedImportVerificationListItem =>
  request.source === undefined || request.source === ImportRequestSource.UserUploaded

export const isExternalImportRequestDetail = (
  request: ImportRequestVerificationDetail
): request is ExternalImportVerificationDetail => isExternalImportRequest(request)

export const isUploadedImportRequestDetail = (
  request: ImportRequestVerificationDetail
): request is UploadedImportVerificationDetail => isUploadedImportRequest(request)

const EXTERNAL_IN_FLIGHT_STATUSES: ImportRequestStatus[] = [
  ImportRequestStatus.Scanning,
  ImportRequestStatus.CopyingExternalCloudLocation,
  ImportRequestStatus.CopyingToAirlockInBucket
]

const UPLOADED_IN_FLIGHT_STATUSES: ImportRequestStatus[] = [
  ImportRequestStatus.Scanning,
  ImportRequestStatus.Processing
]

export const isImportRequestInFlight = (
  request: Pick<ImportRequestVerificationListItem, 'source' | 'status'>
): boolean =>
  isExternalImportRequest(request)
    ? EXTERNAL_IN_FLIGHT_STATUSES.includes(request.status)
    : UPLOADED_IN_FLIGHT_STATUSES.includes(request.status)

export const isImportRequestAwaitingManagerDecision = (
  request: Pick<ImportRequestVerificationListItem, 'source' | 'status'>
): boolean =>
  isExternalImportRequest(request)
    ? request.status === ImportRequestStatus.Pending
    : request.status === ImportRequestStatus.PendingVerification

export const formatImportRequestObjectCountsSummary = (counts?: {
  clean: number
  threatDetected: number
  unsupported: number
}): string => {
  if (counts === undefined) {
    return '-'
  }

  return `${counts.clean} clean, ${counts.threatDetected} threat, ${counts.unsupported} unsupported`
}

export const formatImportRequestScanCountsLabel = (counts?: {
  clean: number
  threatDetected: number
  unsupported: number
}): string | undefined => {
  if (counts === undefined) {
    return undefined
  }

  return `${counts.clean.toLocaleString()} clean · ${counts.threatDetected.toLocaleString()} threat detected · ${counts.unsupported.toLocaleString()} unsupported`
}

export const getImportRequestDetailsTitle = (request: ImportRequestVerificationListItem): string =>
  isExternalImportRequest(request)
    ? request.sourceUri
    : isUploadedImportRequest(request)
    ? request.fileName
    : 'Import request'

export const getImportRequestApprovalModalDescription = (request: ImportRequestVerificationDetail): string => {
  if (isExternalImportRequestDetail(request)) {
    const cleanCount = request.counts?.clean ?? 0

    return `Approve releasing ${cleanCount.toLocaleString()} clean object(s) from ${
      request.sourceUri
    }. Only clean files are imported; threat-detected and unsupported objects are not released.`
  }

  if (!isUploadedImportRequestDetail(request)) {
    return 'Do you want to approve this import request?'
  }

  return `Do you want to approve the request from ${request.requestingUser?.email ?? 'this user'} to import ${
    request.fileName ?? 'this file'
  }?`
}

export const getImportRequestRejectionModalDescription = (request: ImportRequestVerificationDetail): string => {
  if (isExternalImportRequestDetail(request)) {
    return 'Rejecting deletes clean objects from quarantine. Threat-detected objects may be retained per workspace policy. Enter a reason for the rejection; it will be visible to the requester and other Airlock Managers.'
  }

  if (!isUploadedImportRequestDetail(request)) {
    return 'Enter a reason for the rejection; it will be visible to the requester and other Airlock Managers.'
  }

  return `The request to import ${
    request.fileName ?? 'this file'
  } will be rejected. Enter a reason for the rejection; it will be visible to the requester and other Airlock Managers.`
}

export interface ImportRequestScanDetailsInput {
  scanResult?: ImportRequestScanResult
  scanStartedAt?: string
  scanCompletedAt?: string
  scanStatusCode?: string
  counts?: ImportRequestObjectCounts
  formattedTotalSize?: string
}

export const formatImportRequestTotalSize = (bytes?: number): string | undefined => {
  if (bytes === undefined) {
    return undefined
  }

  const kb = bytes / 1024

  return kb < 1 ? `${bytes} B` : `${kb.toFixed(1)} KB`
}

export const deriveExternalImportRequestScanResult = (
  request: ExternalImportVerificationDetail | ExternalImportVerificationListItem
): ImportRequestScanResult => {
  switch (request.status) {
    case ImportRequestStatus.CopyingExternalCloudLocation:
      return ImportRequestScanResult.NotStarted
    case ImportRequestStatus.Scanning:
      return ImportRequestScanResult.InProgress
    case ImportRequestStatus.Failed:
      return ImportRequestScanResult.ScanFailed
    case ImportRequestStatus.Available:
    case ImportRequestStatus.Pending:
    case ImportRequestStatus.CopyingToAirlockInBucket:
    case ImportRequestStatus.Approved:
    case ImportRequestStatus.Rejected:
      if (request.counts === undefined) {
        return ImportRequestScanResult.InProgress
      }

      if (request.counts.clean > 0) {
        return ImportRequestScanResult.Clean
      }

      if (request.counts.threatDetected > 0) {
        return ImportRequestScanResult.ThreatDetected
      }

      return ImportRequestScanResult.Clean
    default:
      return ImportRequestScanResult.NotStarted
  }
}

export const getImportRequestScanDetailsProps = (
  request: ImportRequestVerificationDetail
): ImportRequestScanDetailsInput => {
  if (isUploadedImportRequestDetail(request)) {
    return {
      scanResult: request.scanResult,
      scanStartedAt: request.scanStartedAt,
      scanCompletedAt: request.scanCompletedAt,
      scanStatusCode: request.scanStatusCode
    }
  }

  if (isExternalImportRequestDetail(request)) {
    return {
      scanResult: request.scanResult ?? deriveExternalImportRequestScanResult(request),
      scanStartedAt: request.scanStartedAt,
      scanCompletedAt: request.scanCompletedAt,
      scanStatusCode: request.scanStatusCode,
      counts: request.counts,
      formattedTotalSize: formatImportRequestTotalSize(request.totalSizeInBytes)
    }
  }

  return {}
}
